"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { FileIcon } from "@offstreetparking/design-system/icons/FileIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import { cleanPlate } from "@offstreetparking/domain/entities/vehicle";
import { sanitizeHtml } from "@offstreetparking/domain/html";
import type { IntervalSchedule } from "@offstreetparking/domain/value/interval";
import { PermitStatus } from "@offstreetparking/domain/value/permits";
import { useMemo } from "react";
import type { AdditionalInfoFormData } from "../../CreatePermitWizard/PermitContextProvider/types";
import { useKiosk } from "../../providers/KioskProvider";
import { ParkingAdditionalInfo } from "../parkingDetails";
import type { ProrationInterval } from "../parkingDetails/getProration";
import { CreateAccountCard } from "./components/CreateAccountCard";
import { InvoiceSection } from "./components/InvoiceSection";
import { ParkerTermsSection } from "./components/ParkerTermsSection";
import { ParkingDetailsSection } from "./components/ParkingDetailsSection";
import { ParkingInstructionsSection } from "./components/ParkingInstructionsSection";
import { PermitActionsSection } from "./components/PermitActionsSection";
import { PermitStatusHeader } from "./components/PermitStatusHeader";
import { ShareSection } from "./components/ShareSection";
import { VehicleManagementSection } from "./components/VehicleManagementSection";
import { ViewMyPermitsButton } from "./components/ViewMyPermitsButton";
import type { Permit } from "./types";

export type ConfirmationProps = {
	parcsCodes: Permit["parcsCodes"];
	publicIdentifier: NonNullable<Permit["publicIdentifier"]>;
	title: Permit["title"];
	parkingOptionTitle?: Permit["parkingOptionTitle"];
	permitIntervals: {
		startDateTimeISO: string;
		endDateTimeISO?: string | null;
		schedules?: IntervalSchedule[];
	}[];
	timezone: Permit["timezone"];
	locations: {
		lot?: {
			centerLat?: string;
			centerLng?: string;
			addressLineOne?: string;
			addressLineTwo?: string;
			city?: string;
			state?: string;
			country?: string;
			postalCode?: string;
			useAddress?: boolean;
			name: string;
		};
		name: string;
		order: number;
	}[];
	venue?: Permit["venue"];
	company: Permit["company"];
	vehicles: { id: number; plate: string; state: string }[];
	invoice?: Permit["invoice"];
	paymentSchedule?: Permit["paymentSchedule"];
	fields?: AdditionalInfoFormData["fields"];
	isSmsEnabled: boolean;
	smsExpiryOffsetMinutes: number;
	phoneNumber?: string;
	email?: string;
	instructions?: string | null;
	selfServiceInstructions?: string | null;
	files?: {
		id: number;
		fileName: string | null;
		publicUrl: string | null;
	}[];
	permitStatus: Permit["permitStatus"];
	updateDate: Permit["updateDate"];
	isPaymentPending?: boolean;
	publicAccessToken?: string | null;
	parkerId?: number;
	isEditPermitDisabled: boolean;
	isCancelPermitDisabled: boolean;
	maxVehicleCount?: number;
	addAnotherOption?: () => void;
	selectedPlate?: string;
	printPermit?: boolean;
	parkerTerms: Permit["parkerTerms"];
	createdDate: Permit["createdDate"];
	fullTermIntervals?: ProrationInterval[];
};

export const Confirmation = ({
	parcsCodes,
	publicIdentifier,
	title,
	parkingOptionTitle,
	permitIntervals,
	timezone,
	locations,
	venue,
	company,
	vehicles,
	invoice,
	paymentSchedule,
	fields,
	isSmsEnabled,
	smsExpiryOffsetMinutes,
	phoneNumber,
	email,
	instructions,
	selfServiceInstructions,
	files,
	permitStatus,
	updateDate,
	isPaymentPending = false,
	publicAccessToken,
	parkerId,
	isEditPermitDisabled,
	isCancelPermitDisabled,
	maxVehicleCount,
	addAnotherOption,
	selectedPlate,
	printPermit = false,
	parkerTerms,
	createdDate,
	fullTermIntervals,
}: ConfirmationProps) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const showTitleOnMobile = !!title && (parkingOptionTitle?.trim() || title) !== title;
	const isPaid = (invoice?.total ?? 0) > 0;
	const content = useMemo(() => {
		if (!instructions) {
			return "";
		}
		return sanitizeHtml(instructions);
	}, [instructions]);

	const initialQrIndex = useMemo(() => {
		if (selectedPlate && parcsCodes.length > 0) {
			const normalizedPlate = cleanPlate(selectedPlate);
			const matchingIndex = parcsCodes.findIndex((parcsCode) => {
				if (!parcsCode.plate) {
					return false;
				}
				return parcsCode.plate === normalizedPlate;
			});
			return matchingIndex >= 0 ? matchingIndex : 0;
		}
		return 0;
	}, [selectedPlate, parcsCodes]);

	return (
		<div>
			<div className="flex flex-col gap-4 p-4 sm:p-8">
				{showTitleOnMobile && (
					<Heading As="h2" kind="h2" className="text-center xl:hidden">
						{title}
					</Heading>
				)}
				{!isKiosk && !isPaymentPending && (
					<div
						className={classNames(
							"flex flex-col gap-2 pb-2 print:hidden",
							showTitleOnMobile ? "xl:-mt-4" : "-mt-4",
						)}
					>
						{email && permitStatus !== PermitStatus.VOID && (
							<Text size="sm" className="text-center">
								{isPaid ? (
									<Trans>
										A receipt was sent to <b>{email}</b>
									</Trans>
								) : (
									<Trans>
										A confirmation email was sent to <b>{email}</b>
									</Trans>
								)}
							</Text>
						)}
						<Button
							type="button"
							intent="secondary"
							leftIcon={FileIcon}
							onClick={() => window.print()}
							className="w-full justify-center rounded-lg border-none py-3 text-link underline underline-offset-2 shadow-none hover:bg-neutral"
						>
							{t`Download PDF`}
						</Button>
					</div>
				)}
				<PermitStatusHeader
					parcsCodes={parcsCodes}
					initialQrIndex={initialQrIndex}
					publicIdentifier={publicIdentifier}
					earliestStartDateTimeISO={permitIntervals[0]?.startDateTimeISO}
					latestEndDateTimeISO={permitIntervals[permitIntervals.length - 1]?.endDateTimeISO}
					earliestStartSchedules={permitIntervals[0]?.schedules}
					latestEndSchedules={permitIntervals[permitIntervals.length - 1]?.schedules}
					timezone={timezone}
					permitStatus={permitStatus}
					updateDate={updateDate}
					isPaymentPending={isPaymentPending}
				/>
			</div>
			{!isPaymentPending && (
				<div className="border-neutral-faded border-b empty:hidden print:hidden">
					<CreateAccountCard checkoutEmail={email} publicIdentifier={publicIdentifier} />
					<ShareSection
						publicIdentifier={publicIdentifier}
						isPaid={isPaid}
						email={email}
						isSmsEnabled={isSmsEnabled}
						smsExpiryOffsetMinutes={smsExpiryOffsetMinutes}
						phoneNumber={phoneNumber}
						companyId={company.id}
						permitStatus={permitStatus}
					/>
				</div>
			)}
			<div className="w-full">
				{!isPaymentPending && (
					<ParkingInstructionsSection
						instructions={instructions}
						content={content}
						hasParcsCodes={parcsCodes.length > 0}
						files={files}
						printPermit={printPermit}
					/>
				)}
				<div className="flex flex-col gap-4 p-4 sm:p-8">
					<ParkingDetailsSection
						title={title}
						parkingOptionTitle={parkingOptionTitle}
						permitIntervals={permitIntervals}
						timezone={timezone}
						locations={locations}
						venue={venue}
					/>

					<VehicleManagementSection
						vehicles={vehicles}
						permitStatus={permitStatus}
						publicAccessToken={publicAccessToken}
						parkerId={parkerId}
						isEditPermitDisabled={isEditPermitDisabled || isPaymentPending}
						publicIdentifier={publicIdentifier}
						companyCountry={company.address?.country ?? undefined}
						companyState={company.address?.state ?? undefined}
						maxVehicleCount={maxVehicleCount}
						hasAdditionalInfo={!!fields?.length}
					/>

					<ParkingAdditionalInfo fields={fields} />

					{!isPaymentPending && (
						<InvoiceSection
							invoice={invoice}
							paymentSchedule={paymentSchedule}
							createdDate={createdDate}
							timezone={timezone}
							company={company}
							email={email}
							fullTermIntervals={fullTermIntervals}
						/>
					)}

					<ParkerTermsSection parkerTerms={parkerTerms} acceptedDate={createdDate} />

					{!isPaymentPending && (
						<PermitActionsSection
							permitStatus={permitStatus}
							publicAccessToken={publicAccessToken}
							parkerId={parkerId}
							isEditPermitDisabled={isEditPermitDisabled}
							isCancelPermitDisabled={isCancelPermitDisabled || isKiosk}
							addAnotherOption={addAnotherOption}
							publicIdentifier={publicIdentifier}
							selfServiceInstructions={selfServiceInstructions}
						/>
					)}
				</div>
				<ViewMyPermitsButton />
			</div>
		</div>
	);
};
